import React, { useEffect } from 'react';
import { Box, Fab, Grid } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setShowScrollTop } from '../store/slices/uiSlice';
import { setFilters } from '../store/slices/filtersSlice';
import { loadShoppingListFromStorage } from '../store/slices/shoppingListSlice';
import { getFiltersFromUrl } from '../utils/urlParams';
import FiltersPanel from '../components/Filters/FiltersPanel';
import MobileFilters from '../components/Filters/MobileFilters';
import PriceListTable from '../components/PriceList/PriceListTable';
import { themeColors } from '../theme/colors';
import GearLoader from '../components/Common/GearLoader';

const MainPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { showScrollTop } = useAppSelector((state) => state.ui);
    const [isInitialized, setIsInitialized] = React.useState(false);

    // Инициализация фильтров из URL и списка заказа из localStorage только при первом рендере
    useEffect(() => {
        const urlFilters = getFiltersFromUrl();
        dispatch(setFilters(urlFilters));
        dispatch(loadShoppingListFromStorage());
        setIsInitialized(true);
    }, [dispatch]);

    // Отслеживание изменений URL (например, при использовании кнопок браузера назад/вперед)
    useEffect(() => {
        const handlePopState = () => {
            const urlFilters = getFiltersFromUrl();
            dispatch(setFilters(urlFilters));
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [dispatch]);

    // Отслеживание скролла
    useEffect(() => {
        const handleScroll = () => {
            dispatch(setShowScrollTop(window.pageYOffset > 300));
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [dispatch]);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!isInitialized) {
        return (
            <Box sx={{
                minHeight: '100vh',
                backgroundColor: '#fafbfc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <Box sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <GearLoader size={40} />
                    <div>Загрузка приложения...</div>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: '#fafbfc',
            pt: 2,
            px: { xs: 1, sm: 2 },
        }}>
            <Grid container spacing={1} sx={{ maxWidth: '100%', mx: 'auto' }}>
                {/* Левая панель с фильтрами - скрываем на мобильных */}
                <Grid item xs={12} md={3} lg={2} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <FiltersPanel />
                </Grid>

                {/* Основная область с таблицей (поиск уже включен в таблицу) */}
                <Grid item xs={12} md={9} lg={10}>
                    <PriceListTable />
                </Grid>
            </Grid>

            {/* Мобильные фильтры */}
            <MobileFilters />

            {/* Кнопка "Наверх" */}
            {showScrollTop && (
                <Fab
                    size="medium"
                    onClick={scrollToTop}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(20px)',
                        color: themeColors['600'],
                        border: `1px solid ${themeColors['200']}`,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                            backgroundColor: 'white',
                            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
                        },
                    }}
                >
                    <ArrowUpwardIcon />
                </Fab>
            )}
        </Box>
    );
};

export default MainPage;